import {
	Hand,
	HelpCircle,
	Sparkles,
	GitBranch,
	UserPlus,
	Star,
	PhoneForwarded,
	Flag,
	X,
	GripVertical,
	Cable,
} from "lucide-react";
import type { FlowNode, NodeType } from "@/lib/flow-types";
import { NODE_COLORS } from "@/lib/flow-types";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";

const ICONS: Record<NodeType, React.ElementType> = {
	greeting: Hand,
	question: HelpCircle,
	"ai-response": Sparkles,
	condition: GitBranch,
	"lead-capture": UserPlus,
	qualify: Star,
	handoff: PhoneForwarded,
	end: Flag,
};

interface FlowNodeCardProps {
	node: FlowNode;
	isSelected: boolean;
	onSelect: () => void;
	onDragStart: (e: React.MouseEvent) => void;
	onConnectStart: (e: React.MouseEvent) => void;
	onRemove: () => void;
}

export function FlowNodeCard({
	node,
	isSelected,
	onSelect,
	onDragStart,
	onConnectStart,
	onRemove,
}: FlowNodeCardProps) {
	const Icon = ICONS[node.type];
	const color = NODE_COLORS[node.type];

	return (
		<div
			data-node-id={node.id}
			className="absolute group"
			style={{
				left: node.x,
				top: node.y,
				zIndex: isSelected ? 20 : 10,
			}}
		>
			{/* Connection input port (top) */}
			<div
				data-node-id={node.id}
				className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 bg-[#13151b] opacity-0 group-hover:opacity-100 transition-opacity cursor-crosshair z-30"
				style={{ borderColor: color }}
			/>

			{/* Main card */}
			<div
				className="relative w-[200px] rounded-xl border-2 bg-[#13151b] shadow-md shadow-black/30 hover:shadow-lg hover:shadow-black/40 transition-all cursor-grab active:cursor-grabbing"
				style={{
					borderColor: isSelected ? color : "rgba(255,255,255,0.06)",
					boxShadow: isSelected
						? `0 0 0 3px ${color}25, 0 4px 16px rgba(0,0,0,0.4)`
						: undefined,
				}}
				onMouseDown={(e) => {
					onSelect();
					onDragStart(e);
				}}
			>
				{/* Color accent bar */}
				<div
					className="h-1.5 rounded-t-[10px]"
					style={{ background: color }}
				/>

				<div className="px-3 py-2.5">
					<div className="flex items-center gap-2">
						<div
							className="flex items-center justify-center w-7 h-7 rounded-lg shrink-0"
							style={{
								background: `${color}20`,
							}}
						>
							<Icon
								size={15}
								style={{ color }}
								strokeWidth={2.5}
							/>
						</div>
						<div className="flex-1 min-w-0">
							<p className="text-xs font-semibold text-zinc-200 truncate">
								{node.label}
							</p>
							<p
								className="text-[10px] font-medium"
								style={{ color }}
							>
								{node.type.replace("-", " ")}
							</p>
						</div>
						<GripVertical
							size={14}
							className="text-zinc-600 shrink-0"
						/>
					</div>

					{/* Config preview */}
					{node.config.message && (
						<p className="mt-1.5 text-[10px] text-zinc-500 line-clamp-2 leading-relaxed">
							"{node.config.message}"
						</p>
					)}
					{node.config.condition && !node.config.message && (
						<p className="mt-1.5 text-[10px] text-zinc-500 font-mono">
							if: {node.config.condition}
						</p>
					)}
				</div>

				{/* Remove button */}
				<button
					className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-30"
					onClick={(e) => {
						e.stopPropagation();
						onRemove();
					}}
				>
					<X size={10} strokeWidth={3} />
				</button>
			</div>

			{/* Connection output port (bottom) */}
			<Tooltip>
				<TooltipTrigger asChild>
					<div
						className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 bg-[#13151b] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-crosshair z-30 hover:scale-125"
						style={{ borderColor: color }}
						onMouseDown={(e) => {
							e.stopPropagation();
							onConnectStart(e);
						}}
					>
						<Cable size={9} style={{ color }} />
					</div>
				</TooltipTrigger>
				<TooltipContent side="bottom">
					Drag to connect
				</TooltipContent>
			</Tooltip>
		</div>
	);
}
